import type { Session, TokenStore } from '@stayon/auth-core';
import type { RefreshCoordinator } from '@/shared/auth/refreshCoordinator';
import { refreshCookieSession } from '@/shared/auth/cookieRefresh';
import { classifyRefreshFailure, SessionRefreshError } from './refreshFailure';

export type RefreshState = 'refreshing' | 'reconnecting' | 'authenticated';

interface CookieRefreshOptions {
  baseUrl: string;
  tokenStore: TokenStore;
  refreshCoordinator: RefreshCoordinator;
  onUnauthenticated?: () => void;
  onRefreshState?: (state: RefreshState) => void;
}

/**
 * Erneuert die Heimatsitzung über das Refresh-Cookie. "Single-Flight": wenn 10 Requests
 * gleichzeitig 401 kassieren, läuft der Refresh genau EINMAL und alle 10 warten auf dasselbe
 * Promise. Sonst hagelt's 10× POST /auth/refresh.
 */
export function createCookieRefreshFlight({
  baseUrl,
  tokenStore,
  refreshCoordinator,
  onUnauthenticated,
  onRefreshState,
}: CookieRefreshOptions): () => Promise<Session | null> {
  let refreshInFlight: Promise<Session | null> | null = null;

  async function refreshTokens(): Promise<Session | null> {
    const current = tokenStore.get();
    // Ohne in-memory Session (also ohne CSRF-Token) kann der Interceptor nicht refreshen —
    // das ist der Kaltstart, den hydrate() über den /csrf-Bootstrap erledigt.
    if (!current?.csrfToken) {
      return null;
    }
    onRefreshState?.('refreshing');
    try {
      const session = await refreshCoordinator.runRefresh(async () => {
        // Direkter Cookie-Call ohne diesen Wrapper verhindert 401-Rekursion. Ein wegen
        // Tab-Rotation veralteter CSRF-Token wird genau einmal neu gebootstrappt.
        return refreshCookieSession(baseUrl, tokenStore.get()?.csrfToken ?? current.csrfToken);
      });
      if (session) onRefreshState?.('authenticated');
      return session;
    } catch (error) {
      const failure = classifyRefreshFailure(error);
      if (failure.definitive) {
        tokenStore.clear();
        onUnauthenticated?.();
        return null;
      }
      onRefreshState?.('reconnecting');
      throw new SessionRefreshError(failure, error);
    }
  }

  return () => {
    refreshInFlight ??= refreshTokens().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  };
}
